/**
 * RumPage.tsx — /app/rum — real-user monitoring (pageviews + web vitals)
 *
 * Purpose:    Monthly pageviews vs quota, p75 LCP/INP, JS error rate, and top
 *             pages from GET /v1/rum.
 * Inputs:     rumApi from @/lib/saas-telemetry; billingApi for tier quota
 * Outputs:    RUM summary UI
 * Constraints: Dark-first; WCAG AA. Gateway route rolling out — 404/405/501
 *              renders <ComingOnline/>, never a crash.
 * SOT:        nsentry SaaS completion spec § G-WEB (RUM panel)
 */
import { useEffect, useState } from 'react'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { ComingOnline } from '@/components/ComingOnline'
import { rumApi, type RumSummary } from '@/lib/saas-telemetry'
import { billingApi } from '@/lib/saas-client'
import { TIERS, usagePct, type TierId } from '@/lib/tiers'
import { isComingOnline, type SentryError } from '@/lib/sentry-error'

function vitalClass(value: number | null, good: number, poor: number): string {
  if (value == null) return 'text-brand-muted'
  if (value <= good) return 'text-green-700 dark:text-green-300'
  if (value <= poor) return 'text-yellow-700 dark:text-yellow-300'
  return 'text-red-700 dark:text-red-300'
}

export function RumPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [rum, setRum] = useState<RumSummary | null>(null)
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()

  async function load() {
    setState('loading')
    const [rumRes, billingRes] = await Promise.all([rumApi.get(), billingApi.get()])
    if (!rumRes.ok) {
      setError(rumRes.error)
      setState('error')
      return
    }
    setRum(rumRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState('success')
  }

  useEffect(() => {
    void load()
  }, [])

  if (state === 'error' && isComingOnline(error)) {
    return (
      <div className="p-6 lg:p-8">
        <h1 className="mb-6 text-xl font-semibold text-brand-ink">Real-User Monitoring</h1>
        <ComingOnline feature="RUM" description="Pageviews and Core Web Vitals from your visitors appear here — add the RUM snippet to start collecting." />
      </div>
    )
  }

  const quota = TIERS[tier].rumPageviewsPerMonth
  const pct = rum ? usagePct(rum.pageviewsThisMonth, quota) : 0

  return (
    <div className="p-6 lg:p-8">
      <h1 className="mb-6 text-xl font-semibold text-brand-ink">Real-User Monitoring</h1>

      <AsyncScreen state={state} error={error} onRetry={load}>
        {rum && (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-brand-subtle bg-brand-surface p-5">
                <p className="text-xs uppercase tracking-wide text-brand-muted">Pageviews this month</p>
                <p className="mt-1 text-2xl font-bold text-brand-ink">{rum.pageviewsThisMonth.toLocaleString()}</p>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-surface-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Pageview quota used">
                  <div className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-sky-500'}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-1 text-xs text-brand-muted">{pct}% of {quota.toLocaleString()} ({TIERS[tier].label})</p>
              </div>
              <div className="rounded-xl border border-brand-subtle bg-brand-surface p-5">
                <p className="text-xs uppercase tracking-wide text-brand-muted">p75 LCP</p>
                <p className={`mt-1 text-2xl font-bold ${vitalClass(rum.p75LcpMs, 2500, 4000)}`}>
                  {rum.p75LcpMs != null ? `${(rum.p75LcpMs / 1000).toFixed(2)}s` : '—'}
                </p>
                <p className="mt-1 text-xs text-brand-muted">good ≤2.5s</p>
              </div>
              <div className="rounded-xl border border-brand-subtle bg-brand-surface p-5">
                <p className="text-xs uppercase tracking-wide text-brand-muted">p75 INP</p>
                <p className={`mt-1 text-2xl font-bold ${vitalClass(rum.p75InpMs, 200, 500)}`}>
                  {rum.p75InpMs != null ? `${rum.p75InpMs}ms` : '—'}
                </p>
                <p className="mt-1 text-xs text-brand-muted">good ≤200ms</p>
              </div>
              <div className="rounded-xl border border-brand-subtle bg-brand-surface p-5">
                <p className="text-xs uppercase tracking-wide text-brand-muted">JS error rate</p>
                <p className={`mt-1 text-2xl font-bold ${vitalClass(rum.errorRatePct, 1, 5)}`}>
                  {rum.errorRatePct != null ? `${rum.errorRatePct}%` : '—'}
                </p>
                <p className="mt-1 text-xs text-brand-muted">of pageviews with an error</p>
              </div>
            </div>

            <section className="rounded-xl border border-brand-subtle bg-brand-surface p-5" aria-label="Top pages">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">Top pages</h2>
              {rum.topPages.length === 0 ? (
                <p className="text-sm text-brand-muted">Per-page breakdowns appear once traffic arrives.</p>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {rum.topPages.map((p) => (
                    <li key={p.path} className="flex items-center justify-between py-2 text-sm">
                      <span className="truncate font-mono text-brand-secondary">{p.path}</span>
                      <span className="ml-4 flex-shrink-0 text-brand-muted">{p.views.toLocaleString()} views</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </AsyncScreen>
    </div>
  )
}
